import React, { memo, useEffect, useState } from 'react'
import { shallowEqual } from 'react-redux'

import { VirtualizedList } from '../CarouselTS'

import {
  addQueryParam,
  isShowPath,
  removeDuplicatesByProperty,
} from '../../../../shared/helpers'
import { useAppSelector } from '../../hooks/useStore'
import { contentServiceGet } from '../../lib/utils'
import { Collection } from '../../reducers/types/reducerTypes'
import type { CarouselProps } from './types'

const Carousel = <T,>({
  collection,
  layout,
  onFocus,
  card: Card,
  ttsText,
  index,
  handleInfiniteFetching,
  onArrowPress,
  hideMetadata = true,
  pageDetails = {},
}: CarouselProps<T>) => {
  const { userPid } = useAppSelector(
    (state) => ({
      userPid: state.user.pid,
    }),
    shallowEqual
  )
  const [data, setData] = useState<T[]>(collection?.content || [])
  const [nextUrl, setNextUrl] = useState<string | null>(
    collection?.meta?.links?.next || null
  )
  const [usedFullUrl, setUsedFullUrl] = useState<boolean>(false)
  const dataLength = data.length

  const setNewCollectionData = (newData: any[]) => {
    const key = newData[0]?.feed_cid ? 'feed_cid' : 'cid'
    setData(() => removeDuplicatesByProperty(newData, key))
  }

  const handleCustomInfiniteFetching = () => {
    if (
      handleInfiniteFetching &&
      typeof handleInfiniteFetching === 'function'
    ) {
      handleInfiniteFetching()
      return true
    }
    return false
  }

  const fetchDataFromUrl = async (url: string) => {
    let collectionUrl = url
    if (isShowPath(url)) {
      collectionUrl = addQueryParam(collectionUrl, 'user_id', userPid)
    }
    const response = await contentServiceGet(collectionUrl)
    const nextUrlFromResponse = (response.meta?.links?.next as string) || null
    setUsedFullUrl(true)
    setNextUrl(nextUrlFromResponse)
    const collectionResponse = (
      response.collections?.length > 0 ? response.collections[0] : null
    ) as Collection<T> | null

    if (collectionResponse && collectionResponse.content?.length > 0) {
      setNewCollectionData([...data, ...collectionResponse.content])
    }

    return nextUrlFromResponse
  }

  const fetchMoreData = async (directNextUrl = '') => {
    const isEndOfCollectionReached = !nextUrl && usedFullUrl && !directNextUrl
    if (handleCustomInfiniteFetching() || isEndOfCollectionReached) return

    if (nextUrl || directNextUrl) {
      const nextUrlToUse = (directNextUrl || nextUrl) as string
      await fetchDataFromUrl(nextUrlToUse)
      return
    }

    if (!usedFullUrl && collection?.url && !directNextUrl) {
      const nextPageUrl = await fetchDataFromUrl(collection.url)
      if (nextPageUrl) await fetchMoreData(nextPageUrl)
      return
    }
  }

  useEffect(() => {
    setData(() => collection?.content || [])
  }, [collection.content])

  const sanitizedTitle = collection.title?.replace(/\s+/g, '') || ''
  return (
    <div className="carousel">
      <VirtualizedList<T>
        data={data}
        ttsText={ttsText}
        layout={layout}
        onFocus={onFocus}
        handleInfiniteFetching={fetchMoreData}
        collectionTitle={sanitizedTitle}
      >
        {({ cards, startIndex, onFocus }) =>
          cards.map((card, i) => {
            const key = card.feed_cid ? 'feed_cid' : 'cid'
            return (
              <Card
                focusKey={`${sanitizedTitle}-${startIndex + i}`}
                data={card}
                key={`${index}-${card[key]}`}
                index={startIndex + i}
                onFocus={onFocus}
                layout={layout}
                ttsCounterText={`${
                  data.findIndex((c) => c[key] === card[key]) + 1
                } of ${dataLength}`}
                onArrowPress={onArrowPress}
                hideMetadata={hideMetadata}
                pageDetails={pageDetails}
              />
            )
          })
        }
      </VirtualizedList>
    </div>
  )
}

export default memo(Carousel)
